import { axios } from "./axios";
import { getIdToken } from "../cognito";

const BASE_API_URL = process.env.REACT_APP_GRIGORA_API_URL || "http://localhost:2706";
const GRIGORA_APP_URL = process.env.REACT_APP_GRIGORA_APP_URL || "http://localhost:2707";

const developerAxios = axios.create({
  baseURL: BASE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

developerAxios.interceptors.request.use(
  async (config) => {
    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = token;
    }
    // Let browser set Content-Type with boundary for FormData (multipart)
    if (config.data && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

developerAxios.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = `${GRIGORA_APP_URL}/login?return=${encodeURIComponent(window.location.href)}`;
    }
    return Promise.reject(error);
  }
);

const developerAPI = {
  listApps: () => developerAxios.get("/user/developer/apps").then((data) => data.apps || []),

  getProjects: () => developerAxios.get("/user/projects").then((data) => data.projects || []),

  getApp: (appId) => developerAxios.get(`/user/developer/apps/${appId}`),

  createApp: (body) =>
    developerAxios.post("/user/developer/apps", {
      app_name: body.app_name,
      description: body.description,
      homepage_url: body.homepage_url,
      redirect_uris: body.redirect_uris,
      scopes: body.scopes,
      developer_name: body.developer_name,
    }),

  updateApp: (appId, body) => developerAxios.put(`/user/developer/apps/${appId}`, body),

  uploadAppIcon: (appId, file) => {
    const formData = new FormData();
    formData.append("icon", file);
    return developerAxios.post(`/user/developer/apps/${appId}/icon`, formData);
  },

  uploadAppMedia: (appId, file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    return developerAxios.post(`/user/developer/apps/${appId}/media`, formData);
  },

  deleteApp: (appId) => developerAxios.delete(`/user/developer/apps/${appId}`),

  rotateSecret: (appId) => developerAxios.post(`/user/developer/apps/${appId}/rotate-secret`),

  submitForReview: (appId, body = {}) =>
    developerAxios.post("/user/review-requests", { app_id: appId, ...body }),

  unsubmitForReview: (appId) =>
    developerAxios.delete("/user/review-requests", { data: { app_id: appId } }),



  getAppStats: (appId, days = 30) =>
    developerAxios.get(`/user/developer/apps/${appId}/stats`, { params: { days } }).then((data) => data.stats || []),

  getAppReviews: (appId, params = {}) =>
    developerAxios.get(`/user/developer/apps/${appId}/reviews`, { params }).then((data) => data.reviews || []),

  getAppScripts: (appId) =>
    developerAxios.get(`/user/developer/apps/${appId}/scripts`).then((data) => data.scripts || []),

  getAppScript: (appId, scriptId) =>
    developerAxios.get(`/user/developer/apps/${appId}/scripts/${scriptId}`),

  createAppScript: (appId, body) =>
    developerAxios.post(`/user/developer/apps/${appId}/scripts`, {
      name: body.name,
      script_type: body.scriptType,
      script_placement: body.scriptPlacement,
      code: body.code ?? "",
    }),

  updateAppScript: (appId, scriptId, body) =>
    developerAxios.put(`/user/developer/apps/${appId}/scripts/${scriptId}`, {
      name: body.name,
      script_type: body.scriptType,
      script_placement: body.scriptPlacement,
      code: body.code ?? "",
    }),

  deleteAppScript: (appId, scriptId) =>
    developerAxios.delete(`/user/developer/apps/${appId}/scripts/${scriptId}`),

  getAppDashboardPage: (appId) =>
    developerAxios.get(`/user/developer/apps/${appId}/dashboard-page`).then((data) => data.links || []),

  saveAppDashboardPage: (appId, { type, url }) =>
    developerAxios.put(`/user/developer/apps/${appId}/dashboard-page`, { type, url }).then((data) => data.links || []),

  deleteAppDashboardPage: (appId) =>
    developerAxios.delete(`/user/developer/apps/${appId}/dashboard-page`).then((data) => data.links || []),

  isAuthenticated: async () => (await getIdToken()) !== null,

  getUserFromToken: async () => {
    const token = await getIdToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return {
        sub: payload.sub,
        email: payload.email,
        given_name: payload["custom:given_name"],
        family_name: payload["custom:family_name"],
      };
    } catch {
      return null;
    }
  },

  redirectToLogin: () => {
    window.location.href = `${GRIGORA_APP_URL}/login?return=${encodeURIComponent(window.location.href)}`;
  },
};

export default developerAPI;
